import { Trans } from '@lingui/macro'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import { CancelOutlined, EditOutlined, HourglassTop, PersonAddAlt1 } from '@mui/icons-material'
import { ErrorLabel } from '../forms/ErrorLabel'
import { FC, MouseEvent, useRef } from 'react'
import { ContactParsed } from 'app/services/sfAuth/sfData/sfContact'
import { isNil } from 'lodash'
import { ContactFieldsValues } from './ContactFields'
import { SearchContactsFn } from './useContactDialog'
import { ContactSelectorDialog, ContactSelectorDialogRef } from './ContactSelectorDialog'

interface ContactSelectorProps {
  onSelect?: (contact: ContactParsed) => void
  onDialogClose?: (e: MouseEvent) => void
  onDialogOpen?: (e: MouseEvent) => void
  disabled?: boolean
  preview?: boolean
  values?: ContactParsed
  error?: string
  hasActiveCaseRequest?: boolean
  searchContacts: SearchContactsFn
  addContact: (params: { contact: ContactParsed, isValid: boolean }) => Promise<any>
  createNewContact: (contact: ContactParsed) => Promise<any>
  updateContact: (params: { contact: ContactFieldsValues, isValid: boolean, initialValues: ContactFieldsValues }) => Promise<any>
  currentOrganizationAsDefault?: boolean
}

const ContactSelector: FC<ContactSelectorProps> = ({
  onSelect,
  onDialogClose,
  onDialogOpen,
  searchContacts,
  disabled,
  preview,
  values,
  error,
  hasActiveCaseRequest,
  updateContact,
  createNewContact,
  currentOrganizationAsDefault = false
}) => {
  const dialogRef = useRef<ContactSelectorDialogRef>(null)

  const isContactFound = Boolean(values != null && typeof values === 'object')

  const editIconButton = (
    <Tooltip title={<Trans>Edit</Trans>}>
      <IconButton
        onClick={() => {
          if (!isNil(error)) {
            dialogRef.current?.open('missing-info')
          } else {
            dialogRef.current?.open('contact-info')
          }
        }}
        sx={{
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#17374F1A',
            color: 'primary.main'
          }
        }}
        disabled={disabled}
      >
        <EditOutlined />
      </IconButton>
    </Tooltip>
  )

  const activeRequestIconButton = (
    <Tooltip title={<Trans>CONTACT_INFO_REVIEWED_TOOLTIP</Trans>}>
      <IconButton
        sx={{
          borderRadius: '4px',
          cursor: 'auto',
          backgroundColor: '#17374F1A',
          color: 'primary.main',
          '&:hover': {
            backgroundColor: '#17374F1A',
            color: 'primary.main'
          }
        }}
        disabled={disabled}
      >
        <HourglassTop />
      </IconButton>
    </Tooltip>
  )

  return (
    <>
      <ContactSelectorDialog
        ref={dialogRef}
        onSelect={onSelect}
        onDialogClose={onDialogClose}
        disabled={disabled}
        preview={preview}
        values={values}
        searchContacts={searchContacts}
        createNewContact={createNewContact}
        updateContact={updateContact}
        currentOrganizationAsDefault={currentOrganizationAsDefault}
      />
      {!isContactFound && (
        <Button
          disabled={disabled}
          color='primary'
          variant='contained'
          sx={{ fontSize: '14px', lineHeight: '19px' }}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            dialogRef.current?.open('search')
            if (onDialogOpen != null) {
              onDialogOpen(e)
            }
          }}
        >
          <PersonAddAlt1 sx={{ mr: 1, fontSize: '15px', color: 'white' }} />
          <Trans>ADD_LEGAL_CONTACT_BTN</Trans>
        </Button>
      )}
      {isContactFound && values != null && (
        <Box>
          {!isNil(values.firstName) && !isNil(values.lastName) &&
            <Typography {...(disabled === true ? { color: 'text.disabled' } : {})}>
              <b>{(values.firstName as string) + ' ' + (values.lastName as string)}</b>
            </Typography>}
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <Typography {...(disabled === true ? { color: 'text.disabled' } : {})}>
                {values.email}
              </Typography>
            </Grid>
            <Grid item>
              {hasActiveCaseRequest !== true && <Typography>{editIconButton}</Typography>}
              {hasActiveCaseRequest === true && <Typography>{activeRequestIconButton}</Typography>}
            </Grid>
          </Grid>
        </Box>
      )}
      {error != null && hasActiveCaseRequest !== true && (
        <ErrorLabel
          label={(
            <Grid container alignItems='center'>
              <Grid item sx={{ height: '16px' }}>
                <CancelOutlined sx={{ color: '#E51C40', mr: 1, fontSize: '16px' }} />
              </Grid>
              <Grid item>{error}</Grid>
            </Grid>
          )}
          sx={isContactFound ? { mt: 2 } : { mt: 1 }}
        />
      )}
    </>
  )
}

export default ContactSelector
